import { Injectable } from '@angular/core';
import { Logger } from 'src/services/core/logger/logger.service';

const log = new Logger('Bluetooth-Printer');

// TypeScript definitions for Web Bluetooth API
declare global {
  interface Navigator {
    bluetooth?: {
      requestDevice(options: {
        acceptAllDevices?: boolean;
        optionalServices?: string[];
        filters?: Array<{ services?: string[]; name?: string }>;
      }): Promise<BluetoothDevice>;
    };
  }

  interface BluetoothDevice extends EventTarget {
    id: string;
    name?: string;
    gatt?: BluetoothRemoteGATTServer;
    watchAdvertisements?(): Promise<void>;
    unwatchAdvertisements?(): void;
    addEventListener(
      type: 'gattserverdisconnected',
      listener: (this: this, ev: Event) => any,
      useCapture?: boolean
    ): void;
  }

  interface BluetoothRemoteGATTServer {
    device: BluetoothDevice;
    connected: boolean;
    connect(): Promise<BluetoothRemoteGATTServer>;
    disconnect(): void;
    getPrimaryService(service: string | number): Promise<BluetoothRemoteGATTService>;
    getPrimaryServices(service?: string | number): Promise<BluetoothRemoteGATTService[]>;
  }

  interface BluetoothRemoteGATTService extends EventTarget {
    device: BluetoothDevice;
    uuid: string;
    isPrimary: boolean;
    getCharacteristic(characteristic: string | number): Promise<BluetoothRemoteGATTCharacteristic>;
    getCharacteristics(characteristic?: string | number): Promise<BluetoothRemoteGATTCharacteristic[]>;
  }

  interface BluetoothRemoteGATTCharacteristic extends EventTarget {
    service: BluetoothRemoteGATTService;
    uuid: string;
    properties: {
      read: boolean;
      write: boolean;
      writeWithoutResponse: boolean;
      notify: boolean;
      indicate: boolean;
      broadcast: boolean;
      authenticatedSignedWrites: boolean;
      reliableWrite: boolean;
      writableAuxiliaries: boolean;
    };
    readValue(): Promise<DataView>;
    writeValue(value: BufferSource): Promise<void>;
    writeValueWithoutResponse(value: BufferSource): Promise<void>;
    startNotifications(): Promise<BluetoothRemoteGATTCharacteristic>;
    stopNotifications(): Promise<BluetoothRemoteGATTCharacteristic>;
  }
}

@Injectable({
  providedIn: 'root',
})
export class BluetoothPrinterService {
  private device: BluetoothDevice | null = null;
  private server: BluetoothRemoteGATTServer | null = null;
  private characteristic: BluetoothRemoteGATTCharacteristic | null = null;

  // ESC/POS Commands
  private readonly ESC = '\x1B';
  private readonly GS = '\x1D';
  private readonly LF = '\x0A';

  /**
   * Check if Web Bluetooth API is available
   */
  isAvailable(): boolean {
    return 'bluetooth' in navigator;
  }

  /**
   * Request Bluetooth device and connect
   */
  async connect(): Promise<boolean> {
    try {
      if (!this.isAvailable()) {
        throw new Error('Web Bluetooth API is not available in this browser');
      }

      // Request device with printer service UUID
      // Common printer service UUIDs: 000018f0-0000-1000-8000-00805f9b34fb
      this.device = await (navigator as any).bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          '000018f0-0000-1000-8000-00805f9b34fb', // Printer service
          '0000ff00-0000-1000-8000-00805f9b34fb', // Generic printer
        ],
      });

      if (!this.device || !this.device.gatt) {
        throw new Error('GATT server not available');
      }

      this.server = await this.device.gatt.connect();
      log.debug('Connected to device:', this.device.name || 'Unknown device');

      // Try to find the printer service and characteristic
      const services = await this.server.getPrimaryServices();
      
      for (const service of services) {
        const characteristics = await service.getCharacteristics();
        for (const char of characteristics) {
          // Look for write characteristic
          if (char.properties.write || char.properties.writeWithoutResponse) {
            this.characteristic = char;
            log.debug('Found write characteristic');
            return true;
          }
        }
      }

      // If no specific printer service found, try common characteristics
      if (!this.server) {
        throw new Error('Server not connected');
      }
      
      try {
        const defaultService = await this.server.getPrimaryService('0000ff00-0000-1000-8000-00805f9b34fb');
        this.characteristic = await defaultService.getCharacteristic('0000ff01-0000-1000-8000-00805f9b34fb');
      } catch (error) {
        log.debug('Could not find default service, using first available characteristic');
        // If default service fails, we should have found a characteristic above
        if (!this.characteristic) {
          throw new Error('No suitable printer characteristic found');
        }
      }
      
      return true;
    } catch (error: any) {
      log.error('Bluetooth connection error:', error);
      throw error;
    }
  }

  /**
   * Disconnect from Bluetooth device
   */
  async disconnect(): Promise<void> {
    try {
      if (this.device?.gatt?.connected) {
        this.device.gatt.disconnect();
      }
      this.device = null;
      this.server = null;
      this.characteristic = null;
      log.debug('Disconnected from device');
    } catch (error) {
      log.error('Disconnect error:', error);
    }
  }

  /**
   * Check if device is connected
   */
  isConnected(): boolean {
    return this.server?.connected || false;
  }

  /**
   * Send data to printer
   */
  async sendData(data: Uint8Array): Promise<void> {
    if (!this.characteristic) {
      throw new Error('Not connected to printer');
    }

    try {
      // Split data into chunks of 20 bytes (BLE limit)
      const chunkSize = 20;
      for (let i = 0; i < data.length; i += chunkSize) {
        const chunk = data.slice(i, i + chunkSize);
        
        if (this.characteristic.properties.writeWithoutResponse) {
          await this.characteristic.writeValueWithoutResponse(chunk);
        } else if (this.characteristic.properties.write) {
          await this.characteristic.writeValue(chunk);
        } else {
          throw new Error('Characteristic does not support write');
        }
        
        // Small delay between chunks
        await new Promise(resolve => setTimeout(resolve, 10));
      }
    } catch (error) {
      log.error('Send data error:', error);
      throw error;
    }
  }

  /**
   * Initialize printer
   */
  private initPrinter(): Uint8Array {
    const commands = [
      this.ESC + '@', // Initialize printer
      this.ESC + 'a' + '\x00', // Left align
    ];
    return this.stringToUint8Array(commands.join(''));
  }

  /**
   * Set text alignment
   */
  private setAlign(align: 'left' | 'center' | 'right'): Uint8Array {
    const alignCodes = {
      left: '\x00',
      center: '\x01',
      right: '\x02',
    };
    return this.stringToUint8Array(this.ESC + 'a' + alignCodes[align]);
  }

  /**
   * Set text size
   */
  private setTextSize(width: number = 1, height: number = 1): Uint8Array {
    const size = ((width - 1) << 4) | (height - 1);
    return this.stringToUint8Array(this.GS + '!' + String.fromCharCode(size));
  }

  /**
   * Set bold text
   */
  private setBold(bold: boolean): Uint8Array {
    return this.stringToUint8Array(this.ESC + 'E' + (bold ? '\x01' : '\x00'));
  }

  /**
   * Print line feed
   */
  private lineFeed(lines: number = 1): Uint8Array {
    return this.stringToUint8Array(this.LF.repeat(lines));
  }

  /**
   * Cut paper
   */
  private cutPaper(): Uint8Array {
    return this.stringToUint8Array(this.GS + 'V' + '\x41' + '\x03');
  }

  /**
   * Format number with commas (e.g., 1000 -> 1,000.00)
   */
  private formatMoney(amount: number): string {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /**
   * Convert string to Uint8Array
   */
  private stringToUint8Array(str: string): Uint8Array {
    const arr = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) {
      arr[i] = str.charCodeAt(i);
    }
    return arr;
  }

  /**
   * Combine multiple Uint8Arrays
   */
  private combineArrays(...arrays: Uint8Array[]): Uint8Array {
    const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;
    for (const arr of arrays) {
      result.set(arr, offset);
      offset += arr.length;
    }
    return result;
  }

  /**
   * Generate ESC/POS commands for order receipt
   */
  generateReceipt(order: any): Uint8Array {
    const commands: Uint8Array[] = [];

    // Initialize printer
    commands.push(this.initPrinter());

    // Header
    commands.push(this.setAlign('center'));
    commands.push(this.setTextSize(2, 2));
    commands.push(this.setBold(true));
    commands.push(this.stringToUint8Array('ORDER RECEIPT\n'));
    commands.push(this.setBold(false));
    commands.push(this.setTextSize(1, 1));
    commands.push(this.lineFeed(1));

    // Order Information
    commands.push(this.setAlign('left'));
    commands.push(this.setBold(true));
    commands.push(this.stringToUint8Array('Order ID: '));
    commands.push(this.setBold(false));
    commands.push(this.stringToUint8Array((order.orderId || order.code || 'N/A') + '\n'));

    commands.push(this.setBold(true));
    commands.push(this.stringToUint8Array('Table: '));
    commands.push(this.setBold(false));
    commands.push(this.stringToUint8Array((order.table || 'N/A') + '\n'));

    commands.push(this.setBold(true));
    commands.push(this.stringToUint8Array('Order Type: '));
    commands.push(this.setBold(false));
    commands.push(this.stringToUint8Array((order.orderType || 'N/A') + '\n'));

    // Format dates
    const createdDate = new Date(order.createdTime);
    const updatedDate = new Date(order.updatedTime);
    const formattedCreatedDate = createdDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    const formattedUpdatedDate = updatedDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    commands.push(this.setBold(true));
    commands.push(this.stringToUint8Array('Date & Time: '));
    commands.push(this.setBold(false));
    commands.push(this.stringToUint8Array(formattedCreatedDate + '\n'));

    commands.push(this.setBold(true));
    commands.push(this.stringToUint8Array('Completed: '));
    commands.push(this.setBold(false));
    commands.push(this.stringToUint8Array(formattedUpdatedDate + '\n'));

    const waiterName = order.assignedUsername || order.waiter?.name || 'N/A';
    const kitchenStaff = order.assignedKBStaff || 'N/A';

    commands.push(this.setBold(true));
    commands.push(this.stringToUint8Array('Kitchen Staff: '));
    commands.push(this.setBold(false));
    commands.push(this.stringToUint8Array(kitchenStaff + '\n'));

    commands.push(this.setBold(true));
    commands.push(this.stringToUint8Array('Waiter: '));
    commands.push(this.setBold(false));
    commands.push(this.stringToUint8Array(waiterName + '\n'));

    commands.push(this.lineFeed(1));
    commands.push(this.stringToUint8Array('--------------------------------\n'));

    // Items header
    commands.push(this.setBold(true));
    commands.push(this.stringToUint8Array('ITEMS\n'));
    commands.push(this.setBold(false));
    commands.push(this.stringToUint8Array('--------------------------------\n'));

    // Items
    if (order.dishes) {
      Object.entries(order.dishes).forEach(([category, items]: [string, any]) => {
        if (Array.isArray(items)) {
          items.forEach((item: any) => {
            const itemName = (item.name || 'N/A').substring(0, 20); // Limit length
            const qty = item.quantity || 1;
            const unitPrice = this.formatMoney(item.unitPrice || 0);
            const totalPrice = this.formatMoney(item.totalPrice || 0);

            commands.push(this.stringToUint8Array(`${itemName}\n`));
            commands.push(this.stringToUint8Array(`  Qty: ${qty} x NGN${unitPrice} = NGN${totalPrice}\n`));

            if (item.addons && item.addons.length > 0) {
              item.addons.forEach((addon: any) => {
                const addonName = (addon.name || 'N/A').substring(0, 18);
                const addonQty = addon.quantity || 1;
                const addonTotal = this.formatMoney((addon.price || 0) * addonQty);
                commands.push(this.stringToUint8Array(`  + ${addonName} (${addonQty}x) NGN${addonTotal}\n`));
              });
            }
          });
        }
      });
    }

    commands.push(this.stringToUint8Array('--------------------------------\n'));
    commands.push(this.lineFeed(1));

    // Totals
    commands.push(this.setAlign('right'));
    commands.push(this.stringToUint8Array(`Subtotal: NGN${this.formatMoney(order.subTotal || 0)}\n`));
    commands.push(this.stringToUint8Array(`Tax: NGN${this.formatMoney(order.tax || 0)}\n`));
    commands.push(this.stringToUint8Array(`Service: NGN${this.formatMoney(order.serviceCharge || 0)}\n`));
    
    commands.push(this.setBold(true));
    commands.push(this.setTextSize(1, 2));
    commands.push(this.stringToUint8Array(`TOTAL: NGN${this.formatMoney(order.totalAmount || 0)}\n`));
    commands.push(this.setTextSize(1, 1));
    commands.push(this.setBold(false));

    commands.push(this.lineFeed(2));
    commands.push(this.setAlign('center'));
    commands.push(this.stringToUint8Array('Thank you!\n'));
    commands.push(this.lineFeed(3));

    // Cut paper
    commands.push(this.cutPaper());

    return this.combineArrays(...commands);
  }

  /**
   * Print order receipt via Bluetooth
   */
  async printReceipt(order: any): Promise<void> {
    try {
      // Check if already connected
      if (!this.isConnected()) {
        await this.connect();
      }

      // Generate receipt data
      const receiptData = this.generateReceipt(order);

      // Send to printer
      await this.sendData(receiptData);

      log.debug('Receipt sent to printer successfully');
    } catch (error: any) {
      log.error('Print error:', error);
      throw error;
    }
  }
}

